import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Trophy, Lightbulb, Target, Briefcase, TrendUp } from "@phosphor-icons/react"

const programs = [
  {
    title: "Grassroots Development",
    age: "Ages 6–9",
    icon: Lightbulb,
    focus: "Ball mastery, coordination, enjoyment, discipline",
    outcomes: [
      "Strong technical foundation",
      "Teamwork and respect",
      "Passion for the game"
    ],
    color: "from-gold/10 to-gold/5"
  },
  {
    title: "Foundation Phase",
    age: "Ages 10–12",
    icon: Target,
    focus: "Technical excellence, positional awareness, age‑appropriate conditioning",
    outcomes: [
      "Regional competition exposure",
      "Exchange camps"
    ],
    color: "from-gold/15 to-gold/5"
  },
  {
    title: "Youth Elite Phase",
    age: "Ages 13–15",
    icon: Trophy,
    focus: "Tactical intelligence, mental toughness, game discipline",
    outcomes: [
      "National leagues",
      "International youth tournaments",
      "Scouting visibility"
    ],
    color: "from-gold/20 to-gold/5"
  },
  {
    title: "Professional Preparation",
    age: "Ages 16–18",
    icon: TrendUp,
    focus: "Match intelligence, elite fitness, professionalism",
    outcomes: [
      "International trials",
      "Club partnerships",
      "Scholarship readiness"
    ],
    color: "from-gold/25 to-gold/10"
  },
  {
    title: "Professional Transition",
    age: "18+",
    icon: Briefcase,
    focus: "Career management, contracts, longevity",
    outcomes: [
      "Pro & semi‑pro placements",
      "Collegiate football pathways"
    ],
    color: "from-gold/30 to-gold/15"
  }
]

export function ProgramsPage() {
  // Split programs for image placement
  const grassrootsPrograms = programs.slice(0, 2) // Grassroots & Foundation
  const elitePrograms = programs.slice(2, 3) // Youth Elite
  const proPrograms = programs.slice(3) // Professional Prep & Transition

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory via-ivory to-muted">
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 text-navy text-sm font-medium uppercase tracking-wider mb-6">
              ⚽ Elite Football Programs
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-navy mb-6">
              Football Programs & Player Pathways
            </h1>
            <p className="text-xl text-navy-muted max-w-2xl mx-auto">
              A comprehensive football development journey from grassroots to professional level
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <img src="/images/grassroots-development.jpg" alt="Youth football training" className="rounded-lg shadow-md" />
              <img src="/images/foundation-phase.jpg" alt="Football skills" className="rounded-lg shadow-md" />
              <img src="/images/youth-elite-phase.jpg" alt="Football match" className="rounded-lg shadow-md" />
              <img src="/images/professional-preparation.jpg" alt="Professional pathway" className="rounded-lg shadow-md" />
            </div>
          </div>

          {/* Grassroots & Foundation with Image 5 */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 space-y-8">
              {grassrootsPrograms.map((program, idx) => {
                const Icon = program.icon
                return (
                  <Card 
                    key={idx} 
                    className="border-gold/20 hover:border-gold/40 transition-all duration-300 hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${program.color}`}>
                          <Icon className="text-navy" size={28} weight="duotone" />
                        </div>
                        <div>
                          <CardTitle className="font-serif text-2xl text-navy mb-2">
                            {program.title}
                          </CardTitle>
                          <div className="inline-block px-3 py-1 rounded-full bg-gold/20 text-navy text-sm font-medium">
                            {program.age}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-navy mb-2 uppercase text-xs tracking-wider">Focus</h4>
                          <p className="text-navy-muted">{program.focus}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-navy mb-3 uppercase text-xs tracking-wider">Outcomes</h4>
                          <ul className="space-y-2">
                            {program.outcomes.map((outcome, oidx) => (
                              <li key={oidx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                                <span className="text-navy-muted">{outcome}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
            
            {/* Image 5: Window into training */}
            <div className="hidden lg:block">
              <div className="aspect-[3/4] overflow-hidden rounded-lg sticky top-28">
                <img 
                  src="/images/foundation-phase.jpg" 
                  alt="" 
                  className="w-full h-full object-cover object-top"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>

          {/* Youth Elite Phase */}
          <div className="space-y-8 mb-12">
            {elitePrograms.map((program, idx) => {
              const Icon = program.icon
              return (
                <Card 
                  key={idx} 
                  className="border-gold/20 hover:border-gold/40 transition-all duration-300 hover:shadow-lg"
                >
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg bg-gradient-to-br ${program.color}`}>
                        <Icon className="text-navy" size={28} weight="duotone" />
                      </div>
                      <div>
                        <CardTitle className="font-serif text-2xl text-navy mb-2">
                          {program.title}
                        </CardTitle>
                        <div className="inline-block px-3 py-1 rounded-full bg-gold/20 text-navy text-sm font-medium">
                          {program.age}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-navy mb-2 uppercase text-xs tracking-wider">Focus</h4>
                        <p className="text-navy-muted">{program.focus}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy mb-3 uppercase text-xs tracking-wider">Outcomes</h4>
                        <ul className="space-y-2">
                          {program.outcomes.map((outcome, oidx) => (
                            <li key={oidx} className="flex items-start gap-3">
                              <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                              <span className="text-navy-muted">{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Professional Prep & Transition with Image 7 */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Image 7: Aspirational */}
            <div className="hidden lg:block">
              <div className="aspect-[3/4] overflow-hidden rounded-lg sticky top-28">
                <img 
                  src="/images/professional-transition.jpg" 
                  alt="" 
                  className="w-full h-full object-cover object-top"
                  aria-hidden="true"
                />
              </div>
            </div>
            
            <div className="lg:col-span-2 space-y-8">
              {proPrograms.map((program, idx) => {
                const Icon = program.icon
                return (
                  <Card 
                    key={idx} 
                    className="border-gold/20 hover:border-gold/40 transition-all duration-300 hover:shadow-lg"
                  >
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${program.color}`}>
                          <Icon className="text-navy" size={28} weight="duotone" />
                        </div>
                        <div>
                          <CardTitle className="font-serif text-2xl text-navy mb-2">
                            {program.title}
                          </CardTitle>
                          <div className="inline-block px-3 py-1 rounded-full bg-gold/20 text-navy text-sm font-medium">
                            {program.age}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-navy mb-2 uppercase text-xs tracking-wider">Focus</h4>
                          <p className="text-navy-muted">{program.focus}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-navy mb-3 uppercase text-xs tracking-wider">Outcomes</h4>
                          <ul className="space-y-2">
                            {program.outcomes.map((outcome, oidx) => (
                              <li key={oidx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                                <span className="text-navy-muted">{outcome}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          </div>

          <div className="mt-16 text-center py-12 px-8 rounded-lg bg-gradient-to-r from-navy via-navy-muted to-navy">
            <h3 className="font-serif text-3xl font-semibold text-ivory mb-4">
              ⚽ Beyond Talent, Into Sustainable Football Careers
            </h3>
            <p className="text-ivory/80 text-lg">
              ZOE Football Academy supports players at every stage of their football journey
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
